import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Account, User } from '@wml-insta/shared';
import { ACTIVE_STATUSES, isArchivedStatus } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { useAccountsData } from '../lib/useAccountsData.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { formatNum } from '../lib/format.js';
import { AccountsTable } from '../ui/AccountsTable.js';
import { AccountFilters, EMPTY_FILTERS, filterAccounts, type AccountFiltersValue } from '../ui/AccountFilters.js';
import { ImportAccountsModal } from '../ui/ImportAccountsModal.js';
import { PhoneTabs } from '../ui/PhoneTabs.js';

export function AccountsPage({ me }: { me: User }) {
  const data = useAccountsData();
  const nav = useNavigate();
  // Фильтры сохраняются в localStorage — переживают перезагрузку и переходы.
  const [filters, setFiltersRaw] = useState<AccountFiltersValue>(
    () => ({ ...EMPTY_FILTERS, ...loadPersisted(me.id, 'filters.all', {}) }),
  );
  const setFilters = (next: AccountFiltersValue) => {
    setFiltersRaw(next);
    savePersisted(me.id, 'filters.all', next);
  };
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  // Архивные (бан/выключение) живут в разделе «Архив» — тут только рабочие.
  const workingAccounts = useMemo(
    () => data.accounts.filter((a) => !isArchivedStatus(a.status) || data.archiveGrace.has(a.id)),
    [data.accounts, data.archiveGrace],
  );
  const filtered = useMemo(
    () => filterAccounts(workingAccounts, filters),
    [workingAccounts, filters],
  );

  if (!data.loaded) return <div className="empty-state">Загрузка…</div>;

  return (
    <div className="page-inner page-fill">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>Все аккаунты</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            {formatNum(workingAccounts.length)} рабочих акков · показано {formatNum(filtered.length)}
            {data.accounts.length > workingAccounts.length && (
              <> · <Link to="/archive" style={{ color: 'var(--text-muted)' }}>{formatNum(data.accounts.length - workingAccounts.length)} в архиве →</Link></>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => setImporting(true)}>Импорт из файла</button>
          <button className="btn-primary btn-add-account" onClick={() => setAdding((v) => !v)}>
            {adding ? '✕ Отмена' : '+ Добавить аккаунт'}
          </button>
        </div>
      </div>

      {data.error && <div className="error" style={{ marginBottom: 12 }}>{data.error}</div>}

      <AccountsTable
        accounts={filtered}
        me={me}
        phones={data.phones}
        models={data.models}
        tableKey="all"
          rowActions={filters.unworked ? (a) => (
            a.unworked ? (
              <button
                className="row-open-btn"
                title="Видел: скрыть из необработавших до следующего апдейта отработок"
                style={{ color: 'var(--accent)' }}
                onClick={async () => {
                  const updated = await api<Account>(`/api/accounts/${a.id}/workout-dismiss`, { method: 'POST' });
                  data.applyPatched(updated);
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            ) : null
          ) : undefined}
        draftOpen={adding}
        onDraftClose={() => setAdding(false)}
        onPatched={data.applyPatched}
        onDeleted={data.applyDeleted}
        onCreated={data.applyCreated}
        onOpen={(a) => nav(`/account/${a.id}`)}
        onError={data.setError}
        toolbar={
          <AccountFilters
            value={filters}
            onChange={setFilters}
            accounts={workingAccounts}
            models={data.models}
            phones={data.phones}
            statusOptions={ACTIVE_STATUSES}
          />
        }
      />

      <PhoneTabs
        phones={data.phones}
        activeId={filters.phoneIds.length === 1 ? filters.phoneIds[0]! : null}
        onSelect={(id) => setFilters({ ...filters, phoneIds: id ? [id] : [] })}
      />

      {importing && (
        <ImportAccountsModal
          phones={data.phones}
          models={data.models}
          onClose={() => setImporting(false)}
          onDone={() => data.reload()}
        />
      )}
    </div>
  );
}
